/**
 * Parent-only route guard component
 * Allows only users whose groups claim includes 'parent' to access parent routes.
 * Redirects students to /dashboard and unauthenticated users to /login.
 * Enforces CCM-03: no coin/price UI visible to students.
 *
 * Implements US-006 (T4): ParentGuard route guard
 * Modelled directly on the existing AuthGuard.tsx component.
 *
 * Group membership is read synchronously from useAuth().user?.groups — the Zustand
 * auth store populated by the backend /auth/login response and rehydrated from
 * localStorage on mount. This is the same source used by LoginPage and
 * getPostLoginRoute(); no AWS Amplify call is needed.
 *
 * This is client-side UX routing only; the authoritative authorization is server-side
 * (require_group in the parent dashboard handler).
 */

// BUG-20260625-223103: Remove fetchAuthSession (aws-amplify/auth) and read
// user.groups synchronously from useAuth() store, mirroring AuthGuard.tsx - 2026-06-25
import { Navigate, Outlet, useLocation } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import { Loader2 } from 'lucide-react';
// US-003 T3: import ROUTES so redirect targets reference the central constant, not
// hardcoded strings (enforces TO-LL-013 / TO-LL-014).
import { ROUTES } from '@/constants/routes';

/**
 * ParentGuard — route guard for parent-only pages.
 * Renders the protected child routes (<Outlet />) only for authenticated users
 * whose groups include 'parent'. All other users are redirected appropriately.
 *
 * Groups are sourced from useAuth().user?.groups (Zustand store), which is guaranteed
 * populated before isLoading becomes false (useAuth.ts setUser at line 186 precedes
 * setLoading(false) at line 198).
 *
 * Reusable: US-007 and US-008 wrap the full ParentDashboardPage with this guard.
 */
export function ParentGuard() {
  const { isAuthenticated, isLoading, user } = useAuth();
  const location = useLocation();

  // Show loading spinner while authentication state is resolving.
  // Uses the exact same spinner markup as AuthGuard.tsx.
  // TO-LL-005: isLoading gate prevents premature redirect before store rehydrates.
  if (isLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <div className="text-center">
          <Loader2 className="h-8 w-8 animate-spin mx-auto text-blue-500" />
          <p className="mt-2 text-sm text-gray-600">Checking authentication...</p>
        </div>
      </div>
    );
  }

  // Redirect unauthenticated users to login, preserving the intended destination.
  // US-003 T3: use ROUTES.LOGIN constant (TO-LL-013 compliance).
  if (!isAuthenticated) {
    return <Navigate to={ROUTES.LOGIN} state={{ from: location }} replace />;
  }

  // Redirect authenticated users who are NOT in the 'parent' group (e.g. students)
  // to the student home page — they must never see coin/price UI (CCM-03).
  // Optional chaining handles legacy localStorage users without a groups key
  // gracefully (returns false -> redirect to /dashboard, self-healed by re-login).
  // US-003 T3: use ROUTES.STUDENT_HOME constant (TO-LL-013 compliance).
  if (!user?.groups?.includes('parent')) {
    return <Navigate to={ROUTES.STUDENT_HOME} replace />;
  }

  // Authenticated parent — render the protected child routes.
  return <Outlet />;
}
